import React, { memo, useMemo } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { cn } from '@apps/web/lib/utils';

const REMARK_PLUGINS = [remarkGfm];

type MarkdownAnchorProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
    readonly openInNewTab?: boolean;
};

// Shared anchor for every markdown surface (defaults below plus the
// chat renderer's reference-aware fallback) so link affordance never
// drifts between surfaces. The theme has no link token and `primary`
// is near-foreground, so links need their own hue to be identifiable
// as clickable in running text. External links always open in a new
// tab; `openInNewTab` additionally covers relative/internal hrefs.
export function MarkdownAnchor({
    href,
    openInNewTab = false,
    children,
    className,
    ...props
}: MarkdownAnchorProps) {
    // Case-insensitive scheme, plus protocol-relative (`//host`) URLs,
    // all of which navigate cross-origin and must open in a new tab.
    const isExternal = /^(?:https?:)?\/\//iu.test(href ?? '');
    const opensNewTab = isExternal || openInNewTab;
    return (
        <a
            href={href}
            className={cn(
                'text-blue-600 underline decoration-blue-600/40 underline-offset-2 transition-colors',
                'hover:text-blue-700 hover:decoration-blue-700',
                'dark:text-blue-400 dark:decoration-blue-400/40 dark:hover:text-blue-300 dark:hover:decoration-blue-300',
                opensNewTab &&
                    "after:ml-0.5 after:align-super after:text-[0.65em] after:content-['↗']",
                className,
            )}
            {...props}
            {...(opensNewTab
                ? { target: '_blank', rel: 'noopener noreferrer' }
                : {})}
        >
            {children}
            {opensNewTab && (
                <span className="sr-only"> (opens in a new tab)</span>
            )}
        </a>
    );
}

// Only Phoebe's public asset/doc hosts get the auto-loading <video>
// element and act as poster sources. Any other URL in agent-rendered
// markdown falls through to the normal image path, so untrusted content
// can't make the browser fetch media from an arbitrary host (which would
// leak the viewer's IP and, for <video>, has no referrer protection).
const TRUSTED_MEDIA_HOSTS = new Set(['assets.phoebe.work', 'docs.phoebe.work']);

function isTrustedMediaUrl(url: string | undefined): boolean {
    if (url === undefined) {
        return false;
    }
    try {
        return TRUSTED_MEDIA_HOSTS.has(new URL(url).hostname);
    } catch {
        return false;
    }
}

export const defaultMarkdownComponents: Components = {
    h1: ({ node, ...props }) => (
        <h1 className="my-2 text-base font-semibold" {...props} />
    ),
    h2: ({ node, ...props }) => (
        <h2 className="my-2 text-sm font-semibold" {...props} />
    ),
    h3: ({ node, ...props }) => (
        <h3 className="my-1 text-sm font-medium" {...props} />
    ),
    p: ({ node, ...props }) => (
        <p className="my-1 text-sm leading-relaxed break-words" {...props} />
    ),
    strong: ({ node, ...props }) => (
        <strong className="font-semibold" {...props} />
    ),
    em: ({ node, ...props }) => <em className="italic" {...props} />,
    a: ({ node, ...props }) => <MarkdownAnchor {...props} />,
    img: ({ node, src, alt, title, ...props }) => {
        // Docs embed screen recordings as markdown images pointing at
        // video files; play those with a native player. The markdown
        // title carries the poster thumbnail URL when the docs provide
        // one. Video and poster are gated to trusted media hosts because
        // <video> has no referrerpolicy attribute to fall back on.
        const mediaClassName =
            'border-border my-2 h-auto max-h-96 max-w-[80%] rounded-md border';
        if (
            typeof src === 'string' &&
            /\.(mp4|webm|mov)(?:[?#]|$)/iu.test(src) &&
            isTrustedMediaUrl(src)
        ) {
            const poster =
                title !== undefined && isTrustedMediaUrl(title)
                    ? title
                    : undefined;
            // Without a poster, a media fragment makes the browser
            // decode and show a real frame instead of a blank box.
            const videoSrc = poster || src.includes('#') ? src : `${src}#t=0.1`;
            // `props` is typed as img attributes (loading, srcSet, …) that
            // are not valid on <video>, so it is intentionally not spread
            // here; the docs media set carries no extra attributes anyway.
            return (
                <video
                    src={videoSrc}
                    poster={poster}
                    controls={true}
                    playsInline={true}
                    preload="metadata"
                    aria-label={alt}
                    className={mediaClassName}
                />
            );
        }
        return (
            <img
                {...props}
                src={src}
                alt={alt}
                title={title}
                className={mediaClassName}
                loading="lazy"
                referrerPolicy="no-referrer"
            />
        );
    },
    ul: ({ node, ...props }) => (
        <ul className="my-1 list-disc space-y-0.5 pl-4" {...props} />
    ),
    ol: ({ node, ...props }) => (
        // pl-6 (not pl-4): the number markers render in the padding, and at
        // pl-4 the marker + gap overflow left of the row, where the chat's
        // overflow-x-hidden clips the left edge of each number.
        <ol className="my-1 list-decimal space-y-0.5 pl-6" {...props} />
    ),
    li: ({ node, ...props }) => (
        <li className="text-sm leading-relaxed break-words" {...props} />
    ),
    code: ({ node, ...props }) => (
        <code className="bg-muted rounded px-1 py-0.5 text-xs" {...props} />
    ),
    // w-0 + min-w-full on the scroll wrappers zeroes their intrinsic
    // width contribution while still rendering at full width: without it,
    // a wide table or long code line propagates its min-content width
    // through overflow-x-auto and inflates intrinsically-sized ancestors
    // (e.g. the ScrollArea viewport's display:table wrapper), pushing the
    // whole chat transcript past the panel edge instead of scrolling.
    pre: ({ node, ...props }) => (
        <pre
            className="bg-muted my-2 w-0 min-w-full overflow-x-auto rounded p-2 text-xs"
            {...props}
        />
    ),
    // w-max + per-cell max-w keeps columns readable in narrow panels:
    // with a plain w-full table, auto layout hands a prose column its
    // min-content width (one word per line) once nowrap neighbors eat
    // the available space. Sizing to max-content capped at max-w-64 per
    // cell lets text columns wrap at a readable measure while the
    // wrapper scrolls horizontally.
    table: ({ node, ...props }) => (
        <div className="my-3 w-0 min-w-full overflow-x-auto">
            <table
                className="w-max min-w-full border-collapse text-sm"
                {...props}
            />
        </div>
    ),
    thead: ({ node, ...props }) => <thead className="bg-muted/50" {...props} />,
    tr: ({ node, ...props }) => (
        <tr className="border-border border-b" {...props} />
    ),
    th: ({ node, ...props }) => (
        <th
            className="max-w-64 px-3 py-2 text-left text-xs font-medium break-words"
            {...props}
        />
    ),
    td: ({ node, ...props }) => (
        <td
            className="max-w-64 px-3 py-2.5 align-top text-sm leading-relaxed break-words"
            {...props}
        />
    ),
};

type MarkdownProps = {
    readonly children: string;
    readonly className?: string;
    readonly components?: Components;
} & Omit<
    React.ComponentProps<typeof ReactMarkdown>,
    'children' | 'className' | 'components'
>;

export function Markdown({
    children,
    className,
    components,
    ...props
}: MarkdownProps) {
    return (
        <div className={cn('space-y-1 break-words', className)}>
            <ReactMarkdown
                remarkPlugins={REMARK_PLUGINS}
                components={{
                    ...defaultMarkdownComponents,
                    ...components,
                }}
                {...props}
            >
                {children}
            </ReactMarkdown>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Streaming markdown
// ---------------------------------------------------------------------------

const LIST_ITEM = /^ {0,3}(?:[-*+]|\d{1,9}[.)])[ \t]/;
const BLOCK_QUOTE = /^ {0,3}>/;
const INDENTED = /^(?: {4}|\t)/;
const TABLE_ROW = /^ {0,3}\|/;
const FENCE = /^ {0,3}(?:```|~~~)/;

// A blank line usually ends a markdown block, but not when the next line
// continues the same construct. Splitting a loose list or a lazy blockquote
// would change how it renders, so those keep their blank line.
function continuesBlock(previousLine: string, nextLine: string): boolean {
    if (INDENTED.test(nextLine)) {
        return true;
    }
    if (LIST_ITEM.test(nextLine)) {
        return LIST_ITEM.test(previousLine) || INDENTED.test(previousLine);
    }
    if (BLOCK_QUOTE.test(nextLine)) {
        return BLOCK_QUOTE.test(previousLine);
    }
    if (TABLE_ROW.test(nextLine)) {
        return TABLE_ROW.test(previousLine);
    }
    return false;
}

/**
 * Splits markdown into top-level blocks that render the same separately as
 * they do together.
 *
 * Streaming text only changes in its last block. Rendering blocks
 * independently lets a memoized renderer skip every finished block instead of
 * re-parsing the whole answer for each delta, which is what turns a long
 * answer into quadratic work.
 */
export function splitMarkdownBlocks(value: string): ReadonlyArray<string> {
    const blocks: Array<string> = [];
    let current: Array<string> = [];
    let insideFence = false;
    let previousNonblankLine: string | undefined;
    const lines = value.split('\n');
    for (const [index, line] of lines.entries()) {
        if (FENCE.test(line)) {
            insideFence = !insideFence;
            current.push(line);
            previousNonblankLine = line;
            continue;
        }
        if (insideFence || line.trim() !== '') {
            current.push(line);
            if (line.trim() !== '') {
                previousNonblankLine = line;
            }
            continue;
        }
        let nextIndex = index + 1;
        while (nextIndex < lines.length && lines[nextIndex]?.trim() === '') {
            nextIndex += 1;
        }
        const nextLine = lines[nextIndex];
        if (
            nextLine === undefined ||
            previousNonblankLine === undefined ||
            continuesBlock(previousNonblankLine, nextLine)
        ) {
            current.push(line);
            continue;
        }
        blocks.push(current.join('\n'));
        current = [];
        previousNonblankLine = undefined;
    }
    if (current.length > 0) {
        blocks.push(current.join('\n'));
    }
    return blocks.filter((block) => block.trim() !== '');
}

const TABLE_DELIMITER_ROW =
    /^ {0,3}\|?[ \t]*:?-{1,}:?[ \t]*(?:\|[ \t]*:?-*:?[ \t]*)*\|?[ \t]*$/;
// Markers whose opening half renders as literal punctuation until the closing
// half arrives. Ordered so the two-character run is consumed before backticks.
const STREAMING_INLINE_MARKERS = ['**', '`'] as const;
const TRAILING_MARKER_RUN = /([*_])\1*$/;

function hideUnclosedMarker(value: string, marker: string): string {
    const parts = value.split(marker);
    // An even number of parts means an odd number of markers: the last one
    // has no partner yet.
    return parts.length % 2 === 0 ? parts.slice(0, -1).join(marker) : value;
}

/**
 * Trims the half-written tail of streaming markdown.
 *
 * Markdown only becomes a table, or bold text, once its closing syntax
 * arrives. Rendering the prefix meanwhile shows the reader raw `|` pipes and
 * asterisks, and makes a table's columns jump as each partial row lands.
 * Holding back the unfinished tail costs at most one row of latency and keeps
 * every painted frame something Phoebe actually meant to show.
 */
export function stabilizeStreamingTail(block: string): string {
    const lines = block.split('\n');
    if (lines[0] !== undefined && TABLE_ROW.test(lines[0])) {
        // A row means nothing until its whole line has arrived: cut mid-cell
        // it renders with fewer columns, and the table jumps when the rest
        // lands. Only lines already closed by a newline are settled.
        const settled = lines.slice(0, -1);
        if (!settled.some((line) => TABLE_DELIMITER_ROW.test(line))) {
            return '';
        }
        return settled.join('\n');
    }
    if (FENCE.test(lines[0] ?? '')) {
        // An unclosed fence already renders as code, which is what it will be.
        return block;
    }
    let stabilized = block;
    for (const marker of STREAMING_INLINE_MARKERS) {
        stabilized = hideUnclosedMarker(stabilized, marker);
    }
    // The marker itself arrives one character at a time, so a trailing `*`
    // may be the first half of a `**` that has not landed yet. Only drop it
    // when it is unpaired; an asterisk used as prose stays.
    const trailing = TRAILING_MARKER_RUN.exec(stabilized);
    const trailingMarker = trailing?.[1];
    if (
        trailing !== null &&
        trailingMarker !== undefined &&
        (stabilized.split(trailingMarker).length - 1) % 2 === 1
    ) {
        return stabilized.slice(0, trailing.index);
    }
    return stabilized;
}

const MarkdownBlock = memo(function MarkdownBlock({
    source,
    components,
}: {
    readonly source: string;
    readonly components: Components;
}) {
    return (
        <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={components}>
            {source}
        </ReactMarkdown>
    );
});

/**
 * Markdown for text that arrives a few characters at a time.
 *
 * Renders the same output as `Markdown`, but each finished block is memoized,
 * so a delta only re-parses the block still being written.
 */
export function StreamingMarkdown({
    children,
    className,
    components,
    isStreaming = false,
}: {
    readonly children: string;
    readonly className?: string;
    readonly components?: Components;
    // While true the text is still arriving, so its last block is treated as
    // half written. A finished message renders exactly as `Markdown` does.
    readonly isStreaming?: boolean;
}) {
    const mergedComponents = useMemo(
        () => ({ ...defaultMarkdownComponents, ...components }),
        [components],
    );
    const blocks = useMemo(() => {
        const split = splitMarkdownBlocks(children);
        if (!isStreaming || split.length === 0) {
            return split;
        }
        const stabilized = stabilizeStreamingTail(
            split[split.length - 1] ?? '',
        );
        return stabilized === ''
            ? split.slice(0, -1)
            : [...split.slice(0, -1), stabilized];
    }, [children, isStreaming]);
    return (
        <div className={cn('space-y-1 break-words', className)}>
            {blocks.map((block, index) => (
                <MarkdownBlock
                    // Blocks before the last never change, so their position is
                    // a stable identity while the answer grows.
                    key={index}
                    source={block}
                    components={mergedComponents}
                />
            ))}
        </div>
    );
}
